import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import DatePicker from 'apollo-react/components/DatePicker';
import TextField from 'apollo-react/components/TextField';
import { toast } from 'react-toastify';
import moment from 'moment';
import HcpSignupPageFrame from './HcpSignupPageFrame';
import {
  FormikTextField,
  FormikPasswordInput,
  FormikSelect,
} from '../common/FormikComponent';
import {
  nationalityOptions,
  addressCountryOptions,
  getAddressStateOptions,
  getAddressCityOptions,
  countryCodeDialOptions,
  genderOptions,
  validationConfigDefaults,
  formatHcpFileUploadHint,
  buildHcpSignupIdDocumentFileConfig,
  hcpSignupFormFieldVariant,
} from './hcpSignupDefaults';
import {
  buildGeneralInfoSchema,
  buildMinimalAccountSignupSchema,
  validateIdFile,
  getIdImageValidationOptsFromConfig,
} from './generalInfoSchema';
import {
  postGeneralInfoDraft,
  getSignupDraft,
  postSignupComplete,
  HCP_SIGNUP_DRAFT_STORAGE_KEY,
} from './hcpSignupApi';
import {
  shouldRedirectForward,
  deriveResumeSignupPath,
  isHcpSignupSubmitted,
} from './hcpSignupFlowUtils';
import axiosInstance from '../../axiosInstance';
import FileFieldInput from '../common/FormCrafterFormRenderer/inputs/fields/FileFieldInput';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupLabelWithHint,
} from './HcpSignupFieldLabel';
import { useLicensingExperienceOptional } from '../../contexts/LicensingExperienceContext';
import Checkbox from 'apollo-react/components/Checkbox';
import { Link } from 'react-router-dom';

/** Apollo DatePicker returns a Moment (or null when cleared). Formik + Yup expect YYYY-MM-DD. */
function datePickerValueToIsoDate(v) {
  if (v == null || v === '') return '';
  if (
    typeof v?.format === 'function' &&
    typeof v?.isValid === 'function' &&
    v.isValid()
  ) {
    return v.format('YYYY-MM-DD');
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const y = v.getFullYear();
    const mo = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  }
  if (typeof v === 'string') {
    const t = v.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  }
  return '';
}

/** Apollo DatePicker expects a Moment (or null), not an ISO string — it calls `value.format()`. */
function isoDateToDatePickerValue(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = moment(String(iso).trim().slice(0, 10), 'YYYY-MM-DD', true);
  return m.isValid() ? m : null;
}

function emptyInitial() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    nationality: '',
    idNumber: '',
    countryCode: '',
    phoneNumber: '',
    gender: '',
    addressCountry: '',
    addressState: '',
    addressCity: '',
    addressStreet: '',
    addressZipCode: '',
    categoryId: '',
    tnc: false,
    idImageFile: null,
    existingIdImageId: null,
    uploadedIdLabel: '',
  };
}

/** Map GET /draft API shape into Formik values. */
function draftToFormValues(d) {
  if (!d) return null;
  return {
    firstName: d.firstName || '',
    lastName: d.lastName || '',
    email: d.email || '',
    password: '',
    confirmPassword: '',
    dateOfBirth: d.dateOfBirth ? String(d.dateOfBirth).slice(0, 10) : '',
    nationality: d.nationality || '',
    idNumber: d.idNumber || '',
    countryCode: d.countryCode || '',
    phoneNumber: d.phoneNumber || '',
    gender: d.gender || '',
    addressCountry: d.addressCountry || '',
    addressState: d.addressState || '',
    addressCity: d.addressCity || '',
    addressStreet: d.addressStreet || '',
    addressZipCode: d.addressZipCode || '',
    categoryId:
      d.categoryId != null && d.categoryId !== '' ? String(d.categoryId) : '',
    idImageFile: null,
    existingIdImageId: d.idImage?.id ?? null,
    uploadedIdLabel: d.idImage?.originalName || '',
  };
}

/**
 * Step 1 — General information (HCP sign-up wizard).
 * @param {{ onNext?: (draft: object) => void, validationConfig?: object }} props
 */
export default function HcpSignupGeneralInfoStep({ onNext, validationConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateBackChosen = Boolean(location.state?.hcpSignupNavigateBack);
  const { mode } = useLicensingExperienceOptional();
  const isMinimalSignup = mode === 'license_tracking';
  const mergedConfig = useMemo(
    () => ({ ...validationConfigDefaults, ...validationConfig }),
    [validationConfig]
  );

  const [hasPortalPassword, setHasPortalPassword] = useState(false);

  const [completedPublicId, setCompletedPublicId] = useState(null);

  const validationSchema = useMemo(
    () =>
      isMinimalSignup
        ? buildMinimalAccountSignupSchema(mergedConfig, {
            requirePortalPassword: !hasPortalPassword,
            requireCategory: true,
            requireTerms: true,
          })
        : buildGeneralInfoSchema(mergedConfig, {
            requirePortalPassword: !hasPortalPassword,
            requireCategory: true,
          }),
    [mergedConfig, hasPortalPassword, isMinimalSignup]
  );

  const idDocumentFileConfig = useMemo(
    () => buildHcpSignupIdDocumentFileConfig(mergedConfig),
    [mergedConfig]
  );

  const idImageUploadHint = useMemo(
    () =>
      formatHcpFileUploadHint(
        mergedConfig.idImageAcceptExt,
        mergedConfig.idImageMaxMb,
        mergedConfig.idImageRequired
      ),
    [mergedConfig]
  );

  const [draftId, setDraftId] = useState(null);
  const [loadedValues, setLoadedValues] = useState(null);
  const [bootstrapDone, setBootstrapDone] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  /** Apollo Select under FastField does not refresh menu children when options load later — mount the field only after fetch. */
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/categories/public');
        if (cancel) return;
        setCategoryOptions(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancel) setCategoryOptions([]);
      } finally {
        if (!cancel) setCategoriesLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);
  useEffect(() => {
    let cancel = false;
    (async () => {
      const rawId = window.localStorage.getItem(HCP_SIGNUP_DRAFT_STORAGE_KEY);
      const numericId = rawId ? parseInt(rawId, 10) : NaN;

      if (Number.isNaN(numericId)) {
        if (!cancel) {
          setHasPortalPassword(false);
          setLoadedValues(emptyInitial());
          setBootstrapDone(true);
        }
        return;
      }

      try {
        const res = await getSignupDraft(numericId);
        if (cancel) return;

        if (res?.success && res.data?.draftId) {
          if (
            !isMinimalSignup &&
            shouldRedirectForward(1, res.data, navigateBackChosen, {
              licensingMode: mode,
            })
          ) {
            navigate(
              deriveResumeSignupPath(res.data, { licensingMode: mode }),
              {
                replace: true,
              }
            );
            return;
          }
          if (isMinimalSignup && isHcpSignupSubmitted(res.data)) {
            const pid = res.data.hcpProfile?.publicHcpId;
            if (pid) {
              setCompletedPublicId(pid);
              setBootstrapDone(true);
              return;
            }
          }
          setDraftId(res.data.draftId);
          setHasPortalPassword(res.data.hasPortalPassword === true);
          setLoadedValues({ ...draftToFormValues(res.data) });
        } else {
          window.localStorage.removeItem(HCP_SIGNUP_DRAFT_STORAGE_KEY);
          setHasPortalPassword(false);
          setLoadedValues(emptyInitial());
        }
      } catch (_) {
        if (!cancel) {
          window.localStorage.removeItem(HCP_SIGNUP_DRAFT_STORAGE_KEY);
          setHasPortalPassword(false);
          setLoadedValues(emptyInitial());
        }
      } finally {
        if (!cancel) setBootstrapDone(true);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [mergedConfig, navigate, navigateBackChosen, isMinimalSignup, mode]);

  const initialValues = loadedValues || emptyInitial();

  const handleSubmit = useCallback(
    async (values, { setSubmitting, setErrors, validateForm, setTouched }) => {
      const touched = {};
      Object.keys(values).forEach((k) => {
        touched[k] = true;
      });
      setTouched(touched);

      const formErrors = await validateForm();
      const idOpts = getIdImageValidationOptsFromConfig(mergedConfig);
      const fileErr =
        !isMinimalSignup && values.idImageFile
          ? validateIdFile(values.idImageFile, idOpts)
          : null;

      const combined = {
        ...(formErrors || {}),
        ...(fileErr ? { idImageFile: fileErr } : {}),
      };
      if (Object.keys(combined).length > 0) {
        setErrors(combined);
        setSubmitting(false);
        return;
      }

      try {
        const res = await postGeneralInfoDraft(
          values,
          values.idImageFile instanceof File ? values.idImageFile : null,
          draftId || values.__draftId,
          mergedConfig
        );

        if (!res.success) {
          if (res.errors && typeof res.errors === 'object')
            setErrors(res.errors);
          toast.error(res.message || 'Could not save');
          setSubmitting(false);
          return;
        }

        window.localStorage.setItem(
          HCP_SIGNUP_DRAFT_STORAGE_KEY,
          String(res.data.draftId)
        );
        setDraftId(res.data.draftId);
        setHasPortalPassword(true);

        if (isMinimalSignup) {
          const done = await postSignupComplete(res.data.draftId);
          if (!done.success) {
            toast.error(done.message || 'Could not complete sign-up');
            setSubmitting(false);
            return;
          }
          const pid = done.data?.publicHcpId;
          if (pid) setCompletedPublicId(pid);
          toast.success('Your account has been created.');
          setSubmitting(false);
          return;
        }

        toast.success('Progress saved');

        if (typeof onNext === 'function') onNext(res.data);
        else navigate('/hcp-signup/step-2');
      } catch (e) {
        const payload = e.response?.data;
        if (payload?.errors) setErrors(payload.errors);
        toast.error(payload?.message || 'Could not save draft');
      } finally {
        setSubmitting(false);
      }
    },
    [draftId, mergedConfig, onNext, navigate, isMinimalSignup]
  );

  if (completedPublicId) {
    return (
      <HcpSignupPageFrame>
        <Grid container justifyContent="center" className="signInTitleGrid">
          <div className="authCompletionPanel">
            <h2 className="authCompletionTitle">Sign-up submitted</h2>
            <p className="authCompletionBody">Your unique HCP ID:</p>
            <p className="authCompletionId">{completedPublicId}</p>
            <p
              className="authFormHint"
              style={{ textAlign: 'center', marginTop: 16 }}
            >
              Keep this ID for reference. You can sign in with your email and
              password.
            </p>
            <Link to="/login" className="authCompletionLink">
              Continue to login
            </Link>
          </div>
        </Grid>
      </HcpSignupPageFrame>
    );
  }

  if (!bootstrapDone) {
    return (
      <HcpSignupPageFrame>
        <Grid container justifyContent="center" className="signInTitleGrid">
          <p className="signInDesc">Loading draft…</p>
        </Grid>
      </HcpSignupPageFrame>
    );
  }

  return (
    <HcpSignupPageFrame>
      <Grid container justifyContent="center" className="signInTitleGrid">
        <div>
          <h2 className="signInTitle">Register my account</h2>
          <p className="signInDesc">We&apos;re glad you&apos;re here.</p>
        </div>
      </Grid>

      <Grid container className="formContainer">
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik
              key={draftId || 'new'}
              initialValues={{ ...initialValues, __draftId: draftId }}
              enableReinitialize
              validationSchema={validationSchema}
              validateOnChange
              validateOnBlur
              onSubmit={handleSubmit}
            >
              {({
                values,
                handleSubmit: submit,
                setFieldValue,
                errors,
                touched,
                setFieldTouched,
              }) => {
                const stateOpts = getAddressStateOptions(values.addressCountry);
                const cityOpts = getAddressCityOptions(
                  values.addressCountry,
                  values.addressState
                );

                return (
                  <Form noValidate onSubmit={submit}>
                    <Grid
                      container
                      rowSpacing={0}
                      columnSpacing={2}
                      className="authWizardFieldsGrid"
                    >
                      <Grid item xs={6}>
                        <FormikTextField
                          name="firstName"
                          label={
                            <HcpSignupLabelWithHint
                              text="First name"
                              hint={HCP_SIGNUP_FIELD_HINTS.firstName}
                            />
                          }
                          required
                          fullWidth
                          variant={hcpSignupFormFieldVariant}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormikTextField
                          name="lastName"
                          label={
                            <HcpSignupLabelWithHint
                              text="Last name"
                              hint={HCP_SIGNUP_FIELD_HINTS.lastName}
                            />
                          }
                          required
                          fullWidth
                          variant={hcpSignupFormFieldVariant}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormikTextField
                          name="email"
                          type="email"
                          label={
                            <HcpSignupLabelWithHint
                              text="Email Address"
                              hint={HCP_SIGNUP_FIELD_HINTS.email}
                            />
                          }
                          required
                          fullWidth
                          autoComplete="email"
                          variant={hcpSignupFormFieldVariant}
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text').trim();
                            setFieldValue('email', text);
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        {categoriesLoading ? (
                          <Select
                            label={
                              <HcpSignupLabelWithHint
                                text="Category"
                                hint={HCP_SIGNUP_FIELD_HINTS.category}
                              />
                            }
                            fullWidth
                            disabled
                            variant={hcpSignupFormFieldVariant}
                            value=""
                            helperText="Loading categories…"
                          >
                            <MenuItem value="" disabled>
                              <em>Loading…</em>
                            </MenuItem>
                          </Select>
                        ) : (
                          <FormikSelect
                            name="categoryId"
                            label={
                              <HcpSignupLabelWithHint
                                text="Category"
                                hint={HCP_SIGNUP_FIELD_HINTS.category}
                              />
                            }
                            fullWidth
                            required
                            variant={hcpSignupFormFieldVariant}
                          >
                            <MenuItem value="" disabled>
                              <em>Select category</em>
                            </MenuItem>
                            {categoryOptions.map((cat) => (
                              <MenuItem key={cat.id} value={String(cat.id)}>
                                {cat.name}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <FormikPasswordInput
                          name="password"
                          label={
                            <HcpSignupLabelWithHint
                              text="Password"
                              hint={HCP_SIGNUP_FIELD_HINTS.password}
                            />
                          }
                          fullWidth
                          variant={hcpSignupFormFieldVariant}
                          autoComplete="new-password"
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text').trim();
                            setFieldValue('password', text);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormikPasswordInput
                          name="confirmPassword"
                          label={
                            <HcpSignupLabelWithHint
                              text="Confirm password"
                              hint={HCP_SIGNUP_FIELD_HINTS.confirmPassword}
                            />
                          }
                          fullWidth
                          variant={hcpSignupFormFieldVariant}
                          autoComplete="new-password"
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text').trim();
                            setFieldValue('confirmPassword', text);
                          }}
                        />
                      </Grid>

                      {!isMinimalSignup ? (
                        <>
                          <Grid item xs={6}>
                            <Field name="dateOfBirth">
                              {({ field, meta, form: f }) => (
                                <DatePicker
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="Date of birth"
                                      hint={HCP_SIGNUP_FIELD_HINTS.dateOfBirth}
                                    />
                                  }
                                  name={field.name}
                                  fullWidth
                                  required
                                  value={isoDateToDatePickerValue(field.value)}
                                  onChange={(v) => {
                                    f.setFieldValue(
                                      'dateOfBirth',
                                      datePickerValueToIsoDate(v)
                                    );
                                  }}
                                  onBlur={() =>
                                    setFieldTouched('dateOfBirth', true, false)
                                  }
                                  helperText={
                                    touched.dateOfBirth && errors.dateOfBirth
                                      ? errors.dateOfBirth
                                      : undefined
                                  }
                                  error={Boolean(
                                    touched.dateOfBirth && errors.dateOfBirth
                                  )}
                                  TextFieldProps={{
                                    variant: hcpSignupFormFieldVariant,
                                  }}
                                />
                              )}
                            </Field>
                          </Grid>

                          <Grid item xs={6}>
                            <FormikSelect
                              name="nationality"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Nationality"
                                  hint={HCP_SIGNUP_FIELD_HINTS.nationality}
                                />
                              }
                              fullWidth
                              required
                              variant={hcpSignupFormFieldVariant}
                            >
                              <MenuItem value="" disabled>
                                <em>Select nationality</em>
                              </MenuItem>
                              {nationalityOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </FormikSelect>
                          </Grid>

                          <Grid item xs={6}>
                            <FormikTextField
                              name="idNumber"
                              label={
                                <HcpSignupLabelWithHint
                                  text="ID number"
                                  hint={HCP_SIGNUP_FIELD_HINTS.idNumber}
                                />
                              }
                              required
                              fullWidth
                              variant={hcpSignupFormFieldVariant}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <FormikSelect
                              name="gender"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Gender"
                                  hint={HCP_SIGNUP_FIELD_HINTS.gender}
                                />
                              }
                              fullWidth
                              required={mergedConfig.genderRequired}
                              variant={hcpSignupFormFieldVariant}
                            >
                              <MenuItem value="" disabled>
                                <em>Select gender</em>
                              </MenuItem>
                              {genderOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </FormikSelect>
                          </Grid>

                          <Grid item xs={6}>
                            <FormikSelect
                              name="countryCode"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Country code"
                                  hint={HCP_SIGNUP_FIELD_HINTS.countryCode}
                                />
                              }
                              fullWidth
                              required
                              variant={hcpSignupFormFieldVariant}
                            >
                              <MenuItem value="" disabled>
                                <em>Select code</em>
                              </MenuItem>
                              {countryCodeDialOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </FormikSelect>
                          </Grid>

                          <Grid item xs={6}>
                            <Field name="phoneNumber">
                              {({ field, meta, form: f }) => (
                                <TextField
                                  {...field}
                                  value={field.value || ''}
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="Phone number"
                                      hint={HCP_SIGNUP_FIELD_HINTS.phoneNumber}
                                    />
                                  }
                                  required
                                  fullWidth
                                  variant={hcpSignupFormFieldVariant}
                                  type="tel"
                                  onChange={(e) =>
                                    f.setFieldValue(
                                      'phoneNumber',
                                      String(e.target.value).replace(/\D/g, '')
                                    )
                                  }
                                  helperText={
                                    meta.touched && meta.error
                                      ? meta.error
                                      : undefined
                                  }
                                  error={Boolean(meta.touched && meta.error)}
                                />
                              )}
                            </Field>
                          </Grid>

                          <Grid item xs={12}>
                            <hr className="authFormDivider" />
                          </Grid>

                          <Grid item xs={12}>
                            <p className="authFormSectionHeading">Address</p>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Field name="addressCountry">
                              {({ field, meta, form: f }) => (
                                <Select
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="Country"
                                      hint={
                                        HCP_SIGNUP_FIELD_HINTS.addressCountry
                                      }
                                    />
                                  }
                                  name={field.name}
                                  fullWidth
                                  required={mergedConfig.addressCountryRequired}
                                  variant={hcpSignupFormFieldVariant}
                                  value={field.value}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={
                                    meta.touched && meta.error
                                      ? meta.error
                                      : undefined
                                  }
                                  onBlur={field.onBlur}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    f.setFieldValue('addressCountry', v);
                                    f.setFieldValue('addressState', '');
                                    f.setFieldValue('addressCity', '');
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select country</em>
                                  </MenuItem>
                                  {addressCountryOptions.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                      {o.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Field name="addressState">
                              {({ field, meta, form: f }) => (
                                <Select
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="State / region"
                                      hint={HCP_SIGNUP_FIELD_HINTS.addressState}
                                    />
                                  }
                                  name={field.name}
                                  fullWidth
                                  required={mergedConfig.addressStateRequired}
                                  variant={hcpSignupFormFieldVariant}
                                  value={field.value}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={
                                    meta.touched && meta.error
                                      ? meta.error
                                      : undefined
                                  }
                                  onBlur={field.onBlur}
                                  onChange={(e) => {
                                    f.setFieldValue(
                                      'addressState',
                                      e.target.value
                                    );
                                    f.setFieldValue('addressCity', '');
                                  }}
                                  disabled={!values.addressCountry}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select state / region</em>
                                  </MenuItem>
                                  {stateOpts.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                      {o.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Field name="addressCity">
                              {({ field, meta, form: f }) => (
                                <Select
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="City"
                                      hint={HCP_SIGNUP_FIELD_HINTS.addressCity}
                                    />
                                  }
                                  name={field.name}
                                  fullWidth
                                  required={mergedConfig.addressCityRequired}
                                  variant={hcpSignupFormFieldVariant}
                                  value={field.value}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={
                                    meta.touched && meta.error
                                      ? meta.error
                                      : undefined
                                  }
                                  onBlur={field.onBlur}
                                  onChange={(e) =>
                                    f.setFieldValue(
                                      'addressCity',
                                      e.target.value
                                    )
                                  }
                                  disabled={!values.addressState}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select city</em>
                                  </MenuItem>
                                  {cityOpts.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                      {o.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Field>
                          </Grid>

                          <Grid item xs={12}>
                            <FormikTextField
                              name="addressStreet"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Street"
                                  hint={HCP_SIGNUP_FIELD_HINTS.addressStreet}
                                />
                              }
                              required
                              fullWidth
                              variant={hcpSignupFormFieldVariant}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <FormikTextField
                              name="addressZipCode"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Zip / postal code"
                                  hint={HCP_SIGNUP_FIELD_HINTS.addressZipCode}
                                />
                              }
                              required
                              fullWidth
                              variant={hcpSignupFormFieldVariant}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <hr className="authFormDivider" />
                          </Grid>

                          <Grid item xs={12}>
                            <FileFieldInput
                              id="hcp_signup_step1_id_document"
                              label={
                                <HcpSignupLabelWithHint
                                  text="ID document"
                                  hint={HCP_SIGNUP_FIELD_HINTS.idDocument}
                                  showRequiredAfterText={
                                    mergedConfig.idImageRequired
                                  }
                                />
                              }
                              columnStyle={{ width: '100%', maxWidth: '100%' }}
                              value={
                                values.idImageFile instanceof File
                                  ? [values.idImageFile]
                                  : []
                              }
                              onUpload={(uploaded) => {
                                const list = Array.isArray(uploaded)
                                  ? uploaded
                                  : [uploaded];
                                const next = list.find(
                                  (x) => x instanceof File
                                );
                                if (!next) return;
                                setFieldValue('idImageFile', next);
                                setFieldValue('existingIdImageId', null);
                                setFieldValue('uploadedIdLabel', next.name);
                              }}
                              onDelete={() => {
                                setFieldValue('idImageFile', null);
                                setFieldValue('uploadedIdLabel', '');
                              }}
                              onServerFileDelete={async () => {
                                setFieldValue('existingIdImageId', null);
                                setFieldValue('uploadedIdLabel', '');
                              }}
                              helperText={
                                touched.idImageFile && errors.idImageFile
                                  ? errors.idImageFile
                                  : idImageUploadHint
                              }
                              showError={Boolean(
                                touched.idImageFile && errors.idImageFile
                              )}
                              disabled={false}
                              required={false}
                              fileConfig={idDocumentFileConfig}
                              existingFiles={
                                values.existingIdImageId &&
                                !(values.idImageFile instanceof File)
                                  ? [
                                      {
                                        id: values.existingIdImageId,
                                        name:
                                          values.uploadedIdLabel ||
                                          `Document #${values.existingIdImageId}`,
                                        mimeType: '',
                                        size: 0,
                                      },
                                    ]
                                  : []
                              }
                              eagerUploadEnabled={false}
                              enablePendingDelete={false}
                              initialStagedFiles={[]}
                            />
                          </Grid>
                        </>
                      ) : null}

                      {isMinimalSignup ? (
                        <Grid item xs={12}>
                          <Field name="tnc">
                            {({ field, meta, form: f }) => (
                              <Checkbox
                                label="Terms and Conditions"
                                checked={Boolean(field.value)}
                                onChange={(e) =>
                                  f.setFieldValue('tnc', e.target.checked)
                                }
                                error={Boolean(meta.touched && meta.error)}
                                helperText={
                                  meta.touched && meta.error
                                    ? meta.error
                                    : undefined
                                }
                              />
                            )}
                          </Field>
                        </Grid>
                      ) : null}
                    </Grid>

                    <Button
                      variant="primary"
                      type="submit"
                      className="loginButton"
                    >
                      {isMinimalSignup ? 'Register' : 'Next Step'}
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent="center">
        <p className="signUpText">
          Already have an account?{' '}
          <a href="/login" className="signUpLink">
            Sign In
          </a>
        </p>
      </Grid>
    </HcpSignupPageFrame>
  );
}
